'use client'

import React, { useState, useMemo, useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { useSidebarStore } from '@/hooks/useSidebar'
import { adminNavItems, filterNavItems, type NavItem } from './nav-config'
import { useAuth } from '@/contexts/AuthContext'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useGeneralSettings } from '@/sdk/admin/features/general_settings'
import { useKYCCounts } from '@/sdk/admin/features/kyc'
import { getTenantSlug } from '@/sdk/shared/auth/utils'
import { ChevronRight, ShieldCheck } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

function BrandLogoMark({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <div className={cn("relative flex items-center justify-center rounded-xl bg-gradient-to-br from-[#00E599]/20 to-[#00C675]/10 border border-[#00E599]/30 p-1.5 shadow-sm shrink-0", className)}>
      <ShieldCheck className="h-full w-full text-[#00E599]" />
    </div>
  )
}

function isNavItemActive(href: string | undefined, pathname: string, allHrefs: string[]): boolean {
  if (!href) return false
  if (pathname === href) return true

  // Check sub-paths (e.g. /admin/clients/123 under /admin/clients)
  if (pathname.startsWith(href + '/')) {
    // If another nav item in the sidebar has an exact match or a longer matching prefix for this pathname,
    // this shorter item should NOT be active.
    const hasMoreSpecificMatch = allHrefs.some(
      otherHref =>
        otherHref !== href &&
        (pathname === otherHref || pathname.startsWith(otherHref + '/')) &&
        otherHref.length > href.length
    )
    return !hasMoreSpecificMatch
  }

  return false
}

export function AdminSidebar({
  className,
  forceExpanded = false,
  onItemClick,
}: {
  className?: string
  forceExpanded?: boolean
  onItemClick?: (href?: string) => void
}) {
  const { isCollapsed } = useSidebarStore()
  const { hasFeatureAccess, isLoading: authLoading, roleSlug, features, user } = useAuth()
  const collapsed = forceExpanded ? false : isCollapsed

  const tenantSlug = getTenantSlug() || 'default-tenant'
  const brandTitle = user?.tenant?.name || 'Legacy Global Bank'
  const { generalSettings } = useGeneralSettings(tenantSlug)
  const { data: kycCounts } = useKYCCounts(tenantSlug)
  const pendingKycCount = kycCounts?.pending ?? 3

  const filteredNavItems = useMemo(() => {
    // While auth is loading or features haven't been fetched yet, show all items
    if (authLoading || (roleSlug === null && features === null)) {
      return adminNavItems
    }
    return filterNavItems(adminNavItems, hasFeatureAccess, roleSlug)
  }, [hasFeatureAccess, authLoading, roleSlug, features])

  const allNavHrefs = useMemo(() => {
    const hrefs: string[] = []
    function collect(items: NavItem[]) {
      for (const item of items) {
        if (item.href) hrefs.push(item.href)
        if (item.children) collect(item.children)
      }
    }
    collect(filteredNavItems)
    return hrefs
  }, [filteredNavItems])

  return (
    <TooltipProvider delay={100}>
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 264 }}
        transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
        className={cn(
          'bg-[#0D1527] text-slate-200 flex flex-col z-20 overflow-hidden shrink-0',
          forceExpanded
            ? 'rounded-[20px] my-2 ml-2 h-[calc(100dvh-1rem-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] border border-white/[0.08] shadow-2xl'
            : 'rounded-[24px] my-4 ml-4 h-[calc(100dvh-2rem)] border border-white/[0.08] shadow-2xl',
          className
        )}
      >
        {/* Brand / Logo Header */}
        <div className={cn("flex h-16 items-center shrink-0 px-4", collapsed ? "justify-center px-2" : "justify-start")}>
          {generalSettings?.logoUrl ? (
            <div className="flex items-center justify-center h-full w-full">
              <img
                src={generalSettings.logoUrl}
                alt="Brand Logo"
                className="object-contain transition-all ease-out"
                style={{
                  maxHeight: collapsed ? '40px' : '48px',
                  maxWidth: collapsed ? '40px' : '200px',
                }}
              />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <BrandLogoMark className="h-8 w-8 shrink-0" />
              <AnimatePresence mode="sync">
                {!collapsed && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col min-w-0 select-none"
                  >
                    <span className="font-extrabold text-sm tracking-wide text-white font-sans truncate">
                      {brandTitle}
                    </span>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#00E599]/90">
                      Admin Portal
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Navigation List */}
        <ScrollArea className="flex-[1_1_0] overflow-hidden px-2.5 pb-4">
          <nav className="space-y-1">
            {filteredNavItems.map((item, idx) => {
              const previousItem = idx > 0 ? filteredNavItems[idx - 1] : null
              const isNewSection = item.section && (!previousItem || previousItem.section !== item.section)

              return (
                <React.Fragment key={idx}>
                  {isNewSection && !collapsed && (
                    <div className={cn(
                      "px-3 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 select-none",
                      idx > 0 ? "pt-5" : "pt-2"
                    )}>
                      {item.section}
                    </div>
                  )}
                  <SidebarItem
                    item={item}
                    isCollapsed={collapsed}
                    onItemClick={onItemClick}
                    pendingKycCount={pendingKycCount}
                    allHrefs={allNavHrefs}
                  />
                </React.Fragment>
              )
            })}
          </nav>
        </ScrollArea>
      </motion.aside>
    </TooltipProvider>
  )
}

function SidebarItem({
  item,
  isCollapsed,
  onItemClick,
  pendingKycCount,
  allHrefs,
}: {
  item: NavItem
  isCollapsed: boolean
  onItemClick?: (href?: string) => void
  pendingKycCount?: number
  allHrefs: string[]
}) {
  const pathname = usePathname()
  const hasChildren = item.children && item.children.length > 0

  const isChildActive = useMemo(() => {
    return item.children?.some(child => isNavItemActive(child.href, pathname, allHrefs)) ?? false
  }, [item.children, pathname, allHrefs])

  const [isOpen, setIsOpen] = useState(isChildActive)

  useEffect(() => {
    if (isChildActive) {
      setIsOpen(true)
    }
  }, [isChildActive])

  const badgeValue = item.label === 'KYC Requests' ? pendingKycCount ?? item.badge : item.badge

  if (hasChildren) {
    if (isCollapsed) {
      return (
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                  'group relative flex h-10 w-10 items-center justify-center transition-all duration-200 rounded-[10px]',
                  isChildActive ? 'liquid-glass-active-pill' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
                )}
              >
                <item.icon className="h-5 w-5 shrink-0 relative z-10" />
              </button>
            }
          />
          <TooltipContent side="right" className="font-semibold px-3 py-1.5">
            {item.label}
          </TooltipContent>
        </Tooltip>
      )
    }

    return (
      <div className="flex flex-col">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'group flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-sm font-medium transition-all duration-150',
            isChildActive
              ? 'text-white'
              : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
          )}
        >
          <div className="flex items-center min-w-0">
            <item.icon className={cn(
              "h-5 w-5 shrink-0 transition-colors",
              isChildActive ? "text-white" : "text-slate-300 group-hover:text-white"
            )} />
            <span className={cn(
              "ml-3 truncate whitespace-nowrap",
              isChildActive ? "text-white font-semibold" : "text-slate-200 group-hover:text-white font-medium"
            )}>
              {item.label}
            </span>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {badgeValue !== undefined && (
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F59E0B] text-[11px] font-bold text-white shadow-sm">
                {badgeValue}
              </span>
            )}
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-white transition-colors" />
            </motion.div>
          </div>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="space-y-1 pl-8 pr-1 mt-1">
                {item.children?.map((child, idx) => (
                  <SidebarLink
                    key={idx}
                    item={child}
                    isCollapsed={false}
                    onItemClick={onItemClick}
                    isChild
                    allHrefs={allHrefs}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <SidebarLink
      item={item}
      isCollapsed={isCollapsed}
      onItemClick={onItemClick}
      badgeValue={badgeValue}
      allHrefs={allHrefs}
    />
  )
}

function SidebarLink({
  item,
  isCollapsed,
  onItemClick,
  badgeValue,
  isChild = false,
  allHrefs,
}: {
  item: NavItem
  isCollapsed: boolean
  onItemClick?: (href?: string) => void
  badgeValue?: number | string
  isChild?: boolean
  allHrefs: string[]
}) {
  const pathname = usePathname()
  const isActive = isNavItemActive(item.href, pathname, allHrefs)

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Link
              href={item.href || '#'}
              prefetch={false}
              onClick={(e) => {
                if (onItemClick) {
                  e.preventDefault()
                  onItemClick(item.href)
                }
              }}
              className={cn(
                'group relative flex h-10 w-10 items-center justify-center transition-all duration-200 rounded-[10px] cursor-pointer touch-manipulation select-none',
                isActive
                  ? 'liquid-glass-active-pill font-bold'
                  : 'text-slate-300 hover:bg-white/[0.06] hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5 shrink-0 relative z-10" />
            </Link>
          }
        />
        <TooltipContent side="right" className="font-semibold px-3 py-1.5 flex items-center gap-1.5">
          <span>{item.label}</span>
          {badgeValue !== undefined && (
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#F59E0B] text-[10px] font-bold text-white">
              {badgeValue}
            </span>
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Link
      href={item.href || '#'}
      prefetch={false}
      onClick={(e) => {
        if (onItemClick) {
          e.preventDefault()
          onItemClick(item.href)
        }
      }}
      className={cn(
        'group flex items-center transition-all duration-200 relative cursor-pointer touch-manipulation select-none',
        isChild
          ? 'rounded-[10px] px-2.5 py-2 text-[13px]'
          : 'rounded-[10px] px-3.5 py-2.5 text-sm',
        isActive
          ? 'liquid-glass-active-pill font-bold'
          : 'text-slate-300 hover:bg-white/[0.06] hover:text-white font-medium'
      )}
    >
      <item.icon
        className={cn(
          'shrink-0 transition-colors relative z-10',
          isChild ? 'h-4 w-4' : 'h-5 w-5',
          isActive ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]' : 'text-slate-300 group-hover:text-white'
        )}
      />

      <span
        className={cn(
          'ml-3 truncate whitespace-nowrap overflow-hidden relative z-10 transition-colors',
          isActive ? 'text-white font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]' : 'text-slate-200 group-hover:text-white'
        )}
      >
        {item.label}
      </span>

      {badgeValue !== undefined && (
        <span className="ml-auto relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F59E0B] text-[11px] font-bold text-white shadow-sm">
          {badgeValue}
        </span>
      )}
    </Link>
  )
}


